import { useEffect, useState } from 'preact/hooks';
import { platformUsers, PlatformUserRow } from './adminApi';
import { StepUpModal, StepUpRequiredError, runWithStepUp } from './StepUpModal';

type PendingAction =
  | null
  | { kind: 'disable'; user: PlatformUserRow }
  | { kind: 'enable'; user: PlatformUserRow }
  | { kind: 'resetTotp'; user: PlatformUserRow }
  | { kind: 'changeRole'; user: PlatformUserRow; newRole: string }
  | { kind: 'create' };

const roleOptions = ['super_admin', 'finance', 'support', 'readonly'];

export function PlatformUsersPage() {
  const [users, setUsers] = useState<PlatformUserRow[]>([]);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction>(null);
  const [stepUpFor, setStepUpFor] = useState<(() => void) | null>(null);

  async function load() {
    setErr(null);
    try {
      const r = await platformUsers.listPlatform(includeDisabled);
      setUsers(r.users || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeDisabled]);

  async function runAction(fn: () => Promise<void>) {
    try {
      await runWithStepUp(fn);
      await load();
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => runAction(fn));
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
        <h1 style={{ margin: 0 }}>Platform users</h1>
        <button class="primary" onClick={() => setPending({ kind: 'create' })} style={{ marginLeft: 'auto' }}>
          + New platform user
        </button>
      </div>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Staff accounts with access to this admin panel. Distinct from
        photographer + consumer accounts. Role changes, 2FA resets, and
        disable actions are step-up gated and audit-logged.
      </p>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
        <input
          type="checkbox"
          checked={includeDisabled}
          onChange={(e) => setIncludeDisabled((e.target as HTMLInputElement).checked)}
        />
        <span style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>Include disabled</span>
      </label>

      {err && <div class="error-banner">{err}</div>}
      {info && <div style={{ background: 'rgba(79, 209, 139, 0.15)', border: '1px solid var(--admin-success)', color: '#c2f2d6', padding: 12, borderRadius: 8, marginBottom: 16 }}>{info}</div>}

      <table class="admin-table">
        <thead>
          <tr>
            <th>Username</th>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>2FA</th>
            <th>Last login</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td class="mono">{u.username}</td>
              <td>{u.name}</td>
              <td class="mono">{u.email}</td>
              <td>
                <select
                  value={u.role}
                  onChange={(e) => {
                    const newRole = (e.target as HTMLSelectElement).value;
                    if (newRole === u.role) return;
                    const reason = window.prompt(`Change ${u.username} role to ${newRole}? Reason (optional):`, '');
                    if (reason === null) {
                      (e.target as HTMLSelectElement).value = u.role;
                      return;
                    }
                    runAction(async () => {
                      await platformUsers.updatePlatformRole(u.id, newRole, reason);
                      setInfo(`Role updated to ${newRole}.`);
                    });
                  }}
                  disabled={u.status === 'disabled'}
                >
                  {roleOptions.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </td>
              <td>
                <span class={`badge ${u.status === 'active' ? 'success' : 'failure'}`}>{u.status}</span>
              </td>
              <td>
                {u.totpEnrolled ? (
                  <span class="badge success">enrolled</span>
                ) : (
                  <span class="badge" style={{ background: 'var(--admin-surface-raised)', color: 'var(--admin-text-dim)' }}>
                    not set
                  </span>
                )}
              </td>
              <td class="mono">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
              <td class="mono">{new Date(u.createdAt).toLocaleDateString()}</td>
              <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {u.status === 'active' ? (
                  <button onClick={() => setPending({ kind: 'disable', user: u })}>
                    Disable
                  </button>
                ) : (
                  <button onClick={() => setPending({ kind: 'enable', user: u })}>
                    Enable
                  </button>
                )}
                {u.totpEnrolled && (
                  <button onClick={() => setPending({ kind: 'resetTotp', user: u })}>
                    Reset 2FA
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pending?.kind === 'create' && (
        <CreatePlatformUserModal
          onClose={() => setPending(null)}
          onCreated={(u) => {
            setPending(null);
            setInfo(`Created ${u.username}. They must enroll 2FA on first sign-in.`);
            load();
          }}
          onStepUp={(retry) => {
            setPending(null);
            setStepUpFor(() => retry);
          }}
        />
      )}

      {pending?.kind === 'disable' && (
        <ReasonModal
          title={`Disable ${pending.user.username}?`}
          body="Disabled platform users cannot sign in. They can be re-enabled at any time."
          onCancel={() => setPending(null)}
          onConfirm={(reason) => {
            const user = pending.user;
            setPending(null);
            runAction(async () => {
              await platformUsers.disablePlatform(user.id, reason);
              setInfo(`${user.username} disabled.`);
            });
          }}
        />
      )}

      {pending?.kind === 'enable' && (
        <ReasonModal
          title={`Enable ${pending.user.username}?`}
          body="Clears the disable flag and resets any failed-login lockout."
          onCancel={() => setPending(null)}
          onConfirm={(reason) => {
            const user = pending.user;
            setPending(null);
            runAction(async () => {
              await platformUsers.enablePlatform(user.id, reason);
              setInfo(`${user.username} enabled.`);
            });
          }}
        />
      )}

      {pending?.kind === 'resetTotp' && (
        <ReasonModal
          title={`Reset 2FA for ${pending.user.username}?`}
          body="Their existing TOTP secret + backup codes are cleared. They must re-enroll on next sign-in. Use when the user lost their authenticator device and backup codes."
          onCancel={() => setPending(null)}
          onConfirm={(reason) => {
            const user = pending.user;
            setPending(null);
            runAction(async () => {
              await platformUsers.resetPlatformTOTP(user.id, reason);
              setInfo(`2FA reset for ${user.username}. Next login will force enrollment.`);
            });
          }}
        />
      )}

      {stepUpFor && (
        <StepUpModal
          prompt="This action requires password re-entry."
          onSuccess={() => {
            const retry = stepUpFor;
            setStepUpFor(null);
            retry();
          }}
          onCancel={() => setStepUpFor(null)}
        />
      )}
    </div>
  );
}

function ReasonModal({
  title,
  body,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  onCancel(): void;
  onConfirm(reason: string): void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-surface)',
          border: '1px solid var(--admin-border)',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 500,
        }}
      >
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        <p style={{ color: 'var(--admin-text-dim)' }}>{body}</p>
        <div class="field">
          <label>Reason</label>
          <input
            autoFocus
            value={reason}
            onInput={(e) => setReason((e.target as HTMLInputElement).value)}
            placeholder="Why this action is being taken"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel}>Cancel</button>
          <button class="primary" onClick={() => onConfirm(reason)}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function CreatePlatformUserModal({
  onClose,
  onCreated,
  onStepUp,
}: {
  onClose(): void;
  onCreated(u: PlatformUserRow): void;
  onStepUp(retry: () => void): void;
}) {
  const [f, setF] = useState({
    username: '',
    email: '',
    name: '',
    initialPassword: '',
    role: 'support',
    notes: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    const create = async () => {
      const resp = await platformUsers.createPlatform(f);
      onCreated(resp.user);
    };
    try {
      await runWithStepUp(create);
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        onStepUp(async () => {
          try {
            await runWithStepUp(create);
          } catch (err) {
            setErr(err instanceof Error ? err.message : 'Unknown error');
            setSubmitting(false);
          }
        });
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-surface)',
          border: '1px solid var(--admin-border)',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 520,
        }}
      >
        <h2 style={{ marginTop: 0 }}>New platform user</h2>
        <p style={{ color: 'var(--admin-text-dim)' }}>
          Creates a disabled-until-password-set platform staff account.
          First login forces TOTP enrollment.
        </p>
        {err && <div class="error-banner">{err}</div>}
        <form onSubmit={submit}>
          <div class="field">
            <label>Username</label>
            <input required value={f.username} onInput={(e) => setF({ ...f, username: (e.target as HTMLInputElement).value })} />
          </div>
          <div class="field">
            <label>Email</label>
            <input required type="email" value={f.email} onInput={(e) => setF({ ...f, email: (e.target as HTMLInputElement).value })} />
          </div>
          <div class="field">
            <label>Full name</label>
            <input required value={f.name} onInput={(e) => setF({ ...f, name: (e.target as HTMLInputElement).value })} />
          </div>
          <div class="field">
            <label>Initial password (min 12 chars)</label>
            <input required type="password" minLength={12} value={f.initialPassword} onInput={(e) => setF({ ...f, initialPassword: (e.target as HTMLInputElement).value })} />
          </div>
          <div class="field">
            <label>Role</label>
            <select value={f.role} onChange={(e) => setF({ ...f, role: (e.target as HTMLSelectElement).value })}>
              {roleOptions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div class="field">
            <label>Notes (optional)</label>
            <input value={f.notes} onInput={(e) => setF({ ...f, notes: (e.target as HTMLInputElement).value })} />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" onClick={onClose} disabled={submitting}>Cancel</button>
            <button class="primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
